import React, { useState, useEffect, useCallback } from 'react';import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const API_BASE = '/api/company-images';
const API_KEY = process.env.REACT_APP_COMPANY_IMAGES_API_KEY;

const CATEGORIES = [
  { value: 'founder', label: 'Founder Images' },
  { value: 'service_center', label: 'Service Center' },
  { value: 'gac_showroom', label: 'GAC Showroom' },
  { value: 'future_showroom', label: 'Future Showroom' },
  { value: 'ridara_showroom', label: 'RIDARA Showroom' },
  { value: 'customers', label: 'Customer Gallery' },
  { value: 'staff', label: 'Staff Images' }
];

function CompanyImagesAdmin() {
  const [selectedCategory, setSelectedCategory] = useState('founder');
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form states
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [newImageCaption, setNewImageCaption] = useState('');
  
  const [staffImageFile, setStaffImageFile] = useState(null);
  const [staffImagePreview, setStaffImagePreview] = useState('');
  const [staffImageName, setStaffImageName] = useState('');

  const [formLoading, setFormLoading] = useState(false);
 
  const fetchCategoryData = useCallback(async () => {
  setLoading(true);
  setError(null);
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const res = await fetch(`${API_BASE}/admin/${selectedCategory}`, {
      headers: {
        'X-API-Key': API_KEY,
        'Accept': 'application/json'
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    const result = await res.json();

    if (result.success) {
      setCategoryData(result.data);
      if (result.data?.staffImage?.url) {
        setStaffImagePreview(result.data.staffImage.url);
        setStaffImageName(result.data.staffImage.name || '');
      }
    } else if (res.status === 404) {
      setCategoryData(null);
    } else {
      setError(result.message || 'Failed to load category data');
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      setError('Request timed out');
    } else {
      setError(err.message || 'Failed to fetch data');
    }
    console.error('Fetch error:', err);
  } finally {
    setLoading(false);
  }
}, [selectedCategory]); // Add selectedCategory as dependency

useEffect(() => {
  fetchCategoryData();
}, [fetchCategoryData]);
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleStaffImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setStaffImageFile(file);
      setStaffImagePreview(URL.createObjectURL(file));
    }
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      setError('Please select an image file');
      return;
    }

    setFormLoading(true);
    setError(null);
    setSuccess(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s for upload

    try {
      // Upload image to Firebase Storage
      const imageRef = ref(storage, `company/${selectedCategory}/images/${Date.now()}_${imageFile.name}`);
      await uploadBytes(imageRef, imageFile);
      const imageURL = await getDownloadURL(imageRef);

      // Save to database
      const res = await fetch(`${API_BASE}/admin/${selectedCategory}/add-image`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify({
          url: imageURL,
          caption: newImageCaption,
          order: categoryData?.images?.length || 0
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await res.json();

      if (result.success) {
        setSuccess('Image added successfully!');
        setImageFile(null);
        setImagePreview('');
        setNewImageCaption('');
        setCategoryData(result.data);
        
        // Reset file input
        const fileInput = document.querySelector('input[type="file"][accept="image/*"]');
        if (fileInput) fileInput.value = '';
      } else {
        setError(result.message || 'Failed to add image');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Upload timed out');
      } else {
        setError(err.message || 'Failed to add image');
      }
      console.error('Add error:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteImage = async (imageId, imageURL) => {
    if (!window.confirm('Are you sure you want to delete this image? This will permanently remove it from storage.')) {
      return;
    }

    setFormLoading(true);
    setError(null);
    setSuccess(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      // Delete from Firebase Storage
      if (imageURL) {
        const imageRef = ref(storage, imageURL);
        await deleteObject(imageRef).catch(err => {
          if (err.code !== 'storage/object-not-found') {
            console.warn('Failed to delete from storage:', err);
          }
        });
      }

      // Delete from database
      const res = await fetch(`${API_BASE}/admin/${selectedCategory}/image/${imageId}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': API_KEY
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await res.json();

      if (result.success) {
        setSuccess('Image deleted successfully!');
        setCategoryData(result.data);
      } else {
        setError(result.message || 'Failed to delete image');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timed out');
      } else {
        setError(err.message || 'Failed to delete image');
      }
      console.error('Delete error:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateStaffImage = async (e) => {
    e.preventDefault();
    if (!staffImageFile && !categoryData?.staffImage?.url) {
      setError('Please select a staff image file');
      return;
    }

    setFormLoading(true);
    setError(null);
    setSuccess(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s for upload

    try {
      let staffImageURL = categoryData?.staffImage?.url || null;

      // Upload new image if selected
      if (staffImageFile) {
        // Delete old image if exists
        if (staffImageURL) {
          const oldImageRef = ref(storage, staffImageURL);
          await deleteObject(oldImageRef).catch(err => {
            if (err.code !== 'storage/object-not-found') {
              console.warn('Failed to delete old staff image:', err);
            }
          });
        }

        // Upload new image
        const imageRef = ref(storage, `company/${selectedCategory}/staff/${Date.now()}_${staffImageFile.name}`);
        await uploadBytes(imageRef, staffImageFile);
        staffImageURL = await getDownloadURL(imageRef);
      }

      // Save to database
      const res = await fetch(`${API_BASE}/admin/${selectedCategory}/staff-image`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify({
          url: staffImageURL,
          name: staffImageName
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await res.json();

      if (result.success) {
        setSuccess('Staff image updated successfully!');
        setStaffImageFile(null);
        setCategoryData(result.data);
        setStaffImagePreview(staffImageURL);
        
        // Reset file input
        const fileInput = document.querySelector('input[type="file"][id="staff-image"]');
        if (fileInput) fileInput.value = '';
      } else {
        setError(result.message || 'Failed to update staff image');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Upload timed out');
      } else {
        setError(err.message || 'Failed to update staff image');
      }
      console.error('Update error:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async () => {
    setFormLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const res = await fetch(`${API_BASE}/admin/${selectedCategory}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify({
          isActive: !categoryData?.isActive
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await res.json();

      if (result.success) {
        setSuccess(`Category ${result.data.isActive ? 'activated' : 'deactivated'} successfully!`);
        setCategoryData(result.data);
      } else {
        setError(result.message || 'Failed to update category');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timed out');
      } else {
        setError(err.message || 'Failed to update category');
      }
      console.error('Update error:', err);
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #0F1419, #1A1F2E)',
      color: '#E8E8E8',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <h1 style={{
          fontSize: 'clamp(2rem, 5vw, 3rem)',
          fontWeight: 300,
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          Company Images <span style={{ color: '#C7B381', fontWeight: 600 }}>Admin</span>
        </h1>

        {/* Category Selector */}
        <div style={{
          background: 'rgba(15,20,25,0.8)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid rgba(199,179,129,0.2)'
        }}>
          <label style={{ display: 'block', marginBottom: '0.75rem', color: '#C7B381', fontWeight: 500 }}>
            Select Category
          </label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(199,179,129,0.3)',
              borderRadius: '6px',
              color: '#E8E8E8',
              fontSize: '1rem'
            }}
          >
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        {/* Alerts */}
        {error && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.1)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            color: '#ff6b6b',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            color: '#4ade80',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            {success}
          </div>
        )}

        {loading && !categoryData && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#C7B381' }}>
            Loading...
          </div>
        )}

        {/* Category Status */}
        {categoryData && (
          <div style={{
            background: 'rgba(15,20,25,0.8)',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '2rem',
            border: '1px solid rgba(199,179,129,0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ color: '#C7B381', fontWeight: 500, marginBottom: '0.5rem' }}>
                Category Status
              </div>
              <div style={{ fontSize: '0.9rem', color: '#aaa' }}>
                {categoryData.images?.length || 0} images uploaded
              </div>
            </div>
            <button
              onClick={handleToggleActive}
              disabled={formLoading}
              style={{
                padding: '0.75rem 1.5rem',
                background: categoryData.isActive ? '#22c55e' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: formLoading ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.9rem',
                opacity: formLoading ? 0.6 : 1
              }}
            >
              {categoryData.isActive ? '✓ Active' : '✗ Inactive'}
            </button>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
          gap: '2rem'
        }}>
          {/* Add Gallery Image */}
          <div style={{
            background: 'rgba(15,20,25,0.8)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid rgba(199,179,129,0.2)'
          }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#C7B381' }}>
              Add Gallery Image
            </h2>
            <form onSubmit={handleAddImage}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Select Image File *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(199,179,129,0.3)',
                    borderRadius: '6px',
                    color: '#E8E8E8',
                    fontSize: '0.95rem'
                  }}
                />
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      marginTop: '1rem',
                      maxWidth: '100%',
                      maxHeight: '300px',
                      borderRadius: '8px',
                      objectFit: 'contain'
                    }}
                  />
                )}
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Caption (Optional)
                </label>
                <input
                  type="text"
                  value={newImageCaption}
                  onChange={(e) => setNewImageCaption(e.target.value)}
                  placeholder="Image description..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(199,179,129,0.3)',
                    borderRadius: '6px',
                    color: '#E8E8E8',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={formLoading || !imageFile}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: '#C7B381',
                  color: '#0F1419',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (formLoading || !imageFile) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '1rem',
                  opacity: (formLoading || !imageFile) ? 0.6 : 1
                }}
              >
                {formLoading ? 'Uploading...' : 'Add Image'}
              </button>
            </form>
          </div>

          {/* Update Staff Image */}
          <div style={{
            background: 'rgba(15,20,25,0.8)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid rgba(199,179,129,0.2)'
          }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#C7B381' }}>
              Staff Image
            </h2>
            <form onSubmit={handleUpdateStaffImage}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Select Staff Image File
                </label>
                <input
                  id="staff-image"
                  type="file"
                  accept="image/*"
                  onChange={handleStaffImageChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(199,179,129,0.3)',
                    borderRadius: '6px',
                    color: '#E8E8E8',
                    fontSize: '0.95rem'
                  }}
                />
                {staffImagePreview && (
                  <img
                    src={staffImagePreview}
                    alt="Staff Preview"
                    style={{
                      marginTop: '1rem',
                      width: '150px',
                      height: '150px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '3px solid rgba(199,179,129,0.3)'
                    }}
                  />
                )}
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Staff Name (Optional)
                </label>
                <input
                  type="text"
                  value={staffImageName}
                  onChange={(e) => setStaffImageName(e.target.value)}
                  placeholder="Staff member name..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(199,179,129,0.3)',
                    borderRadius: '6px',
                    color: '#E8E8E8',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={formLoading}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: '#C7B381',
                  color: '#0F1419',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: formLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '1rem',
                  opacity: formLoading ? 0.6 : 1
                }}
              >
                {formLoading ? 'Uploading...' : 'Update Staff Image'}
              </button>
            </form>
          </div>
        </div>

        {/* Gallery Images Grid */}
        {categoryData?.images && categoryData.images.length > 0 && (
          <div style={{
            background: 'rgba(15,20,25,0.8)',
            padding: '2rem',
            borderRadius: '12px',
            marginTop: '2rem',
            border: '1px solid rgba(199,179,129,0.2)'
          }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#C7B381' }}>
              Gallery Images ({categoryData.images.length})
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '1.5rem'
            }}>
              {categoryData.images.sort((a, b) => a.order - b.order).map((img) => (
                <div
                  key={img._id}
                  style={{
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid rgba(199,179,129,0.2)',
                    position: 'relative'
                  }}
                >
                  <img
                    src={img.url}
                    alt={img.caption || ''}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{ padding: '1rem' }}>
                    {img.caption && (
                      <div style={{
                        fontSize: '0.85rem',
                        color: '#aaa',
                        marginBottom: '0.75rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {img.caption}
                      </div>
                    )}
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#666',
                      marginBottom: '0.75rem'
                    }}>
                      Order: {img.order}
                    </div>
                    <button
                      onClick={() => handleDeleteImage(img._id, img.url)}
                      disabled={formLoading}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: formLoading ? 'not-allowed' : 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        opacity: formLoading ? 0.6 : 1
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!categoryData?.images?.length && !loading && (
          <div style={{
            background: 'rgba(15,20,25,0.8)',
            padding: '3rem',
            borderRadius: '12px',
            marginTop: '2rem',
            border: '1px solid rgba(199,179,129,0.2)',
            textAlign: 'center',
            color: '#888'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📷</div>
            <div style={{ fontSize: '1.2rem' }}>No images uploaded yet</div>
            <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
              Upload your first image using the form above
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyImagesAdmin;